import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AccountsSortingService } from '../../../features/accounts/services/accounts-sorting.service';
import { Account } from '../../../domain/account.domain';
import { RestService } from '../../../services/rest/rest.service';
import { AccountsDescriptionService } from '../../../features/accounts/services/accounts-description.service';
import { SecuritySymbol } from '../../../domain/symbol.domain';
import { Currency } from '../../../domain/market.domain';
import {
  RetrieveAccountsResponse,
  SecuritiesTransfersHolding,
  Asset,
  JournalRequest,
  CikTransferRequestItem,
  AgreementPortfolioResponse,
  ValidateAccountResponse,
  TransferTypeResponse,
  SubmitTransferResponse,
  CikRequest,
  TransferStatusResponse, SecuritiesTransfersAccounts, SecuritiesTransfersAccount
} from './securities-transfers.domain';
import { SecuritiesTransferContext } from './securities-transfers-context';

@Injectable()
export class SecuritiesTransfersRestService {
  private readonly restService = inject(RestService);
  private readonly accountsDescriptionService = inject(AccountsDescriptionService);
  private readonly accountsSortingService = inject(AccountsSortingService);

  getSecuritiesTransfersAccounts(accountId: string): Observable<SecuritiesTransfersAccounts> {
    return this.getFromAccounts('SECURITIES_TRANSFERS_ACCOUNT_FILTER')
      .pipe(
        map((data: RetrieveAccountsResponse): SecuritiesTransfersAccounts => {
          let accounts: Array<SecuritiesTransfersAccount> = [];
          let fromAccounts: Array<SecuritiesTransfersAccount> = [];
          if (data.fromAccounts !== undefined && data.fromAccounts.length > 0) {
            accounts = data.fromAccounts;
            fromAccounts = data.fromAccounts
              .filter((account: SecuritiesTransfersAccount): boolean => account.fromAccount !== undefined ? account.fromAccount : true);
          }
          return {
            accountId,
            transferToken: data.transferToken,
            accounts,
            fromAccounts
          };
        }
        ),
        catchError((): Observable<SecuritiesTransfersAccounts> => of({
          accountId,
          transferToken: undefined,
          accounts: [],
          fromAccounts: undefined
        }))
      );
  }

  getFromAccounts(filterName: string): Observable<RetrieveAccountsResponse> {
    const parameters = { params: { filter: filterName } };
    return this.restService.getResource('securitiesTransfers_v2.retrieveAccounts')
      .get<RetrieveAccountsResponse>(parameters)
      .pipe(
        map((data: RetrieveAccountsResponse): RetrieveAccountsResponse => {
          this.accountsDescriptionService.setDescriptions(data.fromAccounts);
          this.accountsSortingService.sort(data.fromAccounts);
          return data;
        })
      );
  }

  validateAccount(id: string, isFromAccount: boolean, transferToken: string): Observable<ValidateAccountResponse> {
    const request = {
      id,
      isFromAccount,
      transferToken
    };
    return this.restService
      .getResource('securitiesTransfers_v2.validateAccount')
      .get<ValidateAccountResponse>({ params: request });
  }

  validateAccountPortfolio(fromAccountId: string, toAccountId: string,
                           transferToken: string, contributionTypeCd: string): Observable<AgreementPortfolioResponse> {
    const request = {
      fromAccountId,
      toAccountId,
      transferToken,
      contributionTypeCd
    };
    return this.restService.getResource('securitiesTransfers_v2.agreementPortfolio')
      .get<AgreementPortfolioResponse>({ params: request });
  }

  getTransferType(fromAccountId: string, toAccountId: string, transferToken: string): Observable<TransferTypeResponse> {
    const request = {
      fromAccountId,
      toAccountId,
      transferToken
    };
    return this.restService.getResource('securitiesTransfers_v2.transferType')
      .get<TransferTypeResponse>({ params: request });
  }

  retrieveSecurity(account: Account, symbol: SecuritySymbol, transferToken: string,
                   contributionType: string): Observable<SecuritiesTransfersHolding> {
    const request = {
      accountId: account.id,
      symbol: symbol.symbol,
      market: symbol.market,
      type: symbol.symbolType,
      transferToken,
      contributionTypeCd: contributionType
    };
    return this.restService.getResource('securitiesTransfers_v2.security')
      .get<SecuritiesTransfersHolding>({ params: request });
  }

  /**
   * The 'makeContribution' method is for CIK transfer types.
   * It is closely related to the 'submitTransfer' which handles JOURNAL transfer types.
   */
  makeContribution(request: CikRequest): Observable<SubmitTransferResponse> {
    return this.restService.getResource('securitiesTransfers_v2.submitTransferRequest')
      .post<CikRequest, SubmitTransferResponse>(request);
  }

  /**
   * The 'submitTransfer' method is for JOURNAL transfer types.
   */
  submitTransfer(request: JournalRequest): Observable<SubmitTransferResponse> {
    return this.restService.getResource('securitiesTransfers_v2.submitTransferRequest')
      .post<JournalRequest, SubmitTransferResponse>(request);
  }

  // Create JOURNAL specific request object for final transfer submission.
  createJournalTransferRequest(validateOnly: boolean, context: SecuritiesTransferContext): JournalRequest {
    const securities = context.selectedSecurities.map((security: SecuritiesTransfersHolding): Asset => ({
      securityId: security.securityId,
      selectedQuantity: security.inputQuantity
    }));
    return {
      validateOnly,
      transferToken: context.transferToken,
      fromAccountId: context.transferFromAccount.id,
      toAccountId: context.transferToAccount.id,
      exchangeRate: context.getExchangeRate(context.transferFromAccount.currency, context.transferToAccount.currency),
      totalMarketValueCad: context.calculateTotalTransferValue(Currency.CAD),
      totalMarketValueUsd: context.calculateTotalTransferValue(Currency.USD),
      cashValue: context.cashAmount,
      assetCollection: securities
    };
  }

  // Create CIK specific request object for final transfer submission.
  createCikTransferRequest(validateOnly: boolean, context: SecuritiesTransferContext): CikRequest {
    const securities = context.selectedSecurities.map((security: SecuritiesTransfersHolding): Asset => ({
      securityId: security.securityId,
      selectedQuantity: security.inputQuantity
    }));
    const contributionRequest = context.selectedSecurities
      .map((security: SecuritiesTransfersHolding): CikTransferRequestItem => ({
        fromAccountId: context.transferFromAccount.id,
        toAccountId: context.transferToAccount.id,
        contributionType: context.getContributionType(),
        totalQuantity: security.quantity,
        security: {
          symbol: security.symbol.tickerSymbol,
          type: security.symbol.type,
          market: security.symbol.market
        },
        securityName: security.name,
        quantity: security.inputQuantity,
        lastPrice: security.lastPrice,
        fxRate: security.fxRate,
        currency: security.currency,
        securityId: security.securityId,
        cusip: security.cusip,
        key: security.key
      }));
    return {
      validateOnly,
      transferToken: context.transferToken,
      fromAccountId: context.transferFromAccount.id,
      toAccountId: context.transferToAccount.id,
      exchangeRate: context.getExchangeRate(context.transferFromAccount.currency, context.transferToAccount.currency),
      totalMarketValueCad: context.calculateTotalTransferValue(Currency.CAD),
      totalMarketValueUsd: context.calculateTotalTransferValue(Currency.USD),
      cashValue: context.cashAmount,
      assetCollection: securities,
      contributionType: context.getContributionType(),
      contributionRequest
    };
  }

  getTransferStatus(referenceId: string): Observable<TransferStatusResponse> {
    const request = {
      referenceId
    };
    return this.restService.getResource('securitiesTransfers_v2.transferStatus')
      .get<TransferStatusResponse>({ params: request });
  }
}

